import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote, Empty, RemoveButton, SelectMenu } from '../components/ui';
import { money, fmtDate } from '../lib/format';

const METHODS = ['cheque', 'cash', 'card', 'bank_transfer'];
// Cash has no reference number to record; the other methods each have one,
// just under a different name.
const REFERENCE_LABELS = { cheque: 'Cheque #', card: 'Transaction #', bank_transfer: 'Transaction #' };

export default function InvoiceBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, run } = useApp();

  const { data: project, error, loading, reload } = useAsync(() => api.get('/projects/' + id), [id]);

  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState(METHODS[0]);
  const [payReference, setPayReference] = useState('');
  const [busy, setBusy] = useState(false);
  const referenceLabel = REFERENCE_LABELS[payMethod];

  const setMethod = (v) => {
    setPayMethod(v);
    if (!REFERENCE_LABELS[v]) setPayReference(''); // cash carries no reference
  };

  useEffect(() => {
    if (!project) return;
    const inv = project.invoice;
    setInvoice(inv || null);
    setLineItems(inv?.lineItems?.length
      ? inv.lineItems.map(x => ({ ...x }))
      : project.standalone ? [{ description: '', amount: 0 }] : [{
          description: `${project.proposal?.sections?.[0]?.name || 'Project work'} — per signed agreement`,
          amount: project.proposal?.total || 0,
        }]);
  }, [project]);

  const total = useMemo(
    () => lineItems.reduce((s, x) => s + (Number(x.amount) || 0), 0),
    [lineItems],
  );

  if (loading) return <Spinner label="Loading invoice" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;

  const patch = (i, k, v) => setLineItems(l => l.map((x, j) => j === i ? { ...x, [k]: v } : x));

  async function saveInvoice() {
    const saved = invoice
      ? await api.put('/invoices/' + invoice.id, {
          lineItems, subtotal: total, total, balance: total - (invoice.amountPaid || 0),
        })
      : await api.post('/invoices', { projectId: id, lineItems });
    setInvoice(saved);
    return saved;
  }

  const onPreview = () => run(async () => {
    setBusy(true);
    const inv = await saveInvoice();
    setBusy(false);
    window.open(`/inv/${inv.token}`, '_blank', 'noopener');
  });

  const onSend = () => run(async () => {
    setBusy(true);
    const inv = await saveInvoice();
    const r = await api.post(`/invoices/${inv.id}/send`);
    navigator.clipboard?.writeText(`${location.origin}/inv/${r.invoice.token}`).catch(() => {});
    setBusy(false);
    if (!r.email) toast('Link copied — no email on file, so nothing was sent');
    else if (!r.email.delivered) toast(`Link copied, but the email failed: ${r.email.error || 'unknown error'}`);
    else toast('Invoice sent — link copied');
    navigate(project.standalone ? '/documents' : `/project/${id}`);
  });

  const recordPayment = () => run(async () => {
    const amt = parseFloat(payAmount || '0');
    if (!amt) return toast('Enter an amount');
    await api.post(`/invoices/${invoice.id}/payments`, {
      amount: amt, method: payMethod,
      note: referenceLabel ? payReference : '',
    });
    setPayAmount(''); setPayReference('');
    toast('Payment recorded');
    reload();
  });

  return (
    <>
      <PageHeader
        title="Invoice"
        sub={`${project.number} · ${project.customer?.name || ''}`}
        back={project.standalone ? '/documents' : `/project/${id}`}
      />
      <div className="view">
        <Card>
          <SectionTitle>Line items</SectionTitle>
          <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
            {lineItems.map((x, i) => (
              <div className="scope-item" key={i}>
                <textarea rows={1} placeholder="Description" aria-label={`Line ${i + 1} description`}
                  value={x.description} onChange={e => patch(i, 'description', e.target.value)} />
                <span className="amt">
                  <input type="number" min="0" step="0.01" placeholder="0" aria-label={`Line ${i + 1} amount`}
                    value={x.amount || ''} onChange={e => patch(i, 'amount', parseFloat(e.target.value || '0'))} />
                </span>
                <RemoveButton label={`Remove line ${i + 1}`}
                  onClick={() => setLineItems(l => l.filter((_, j) => j !== i))} />
              </div>
            ))}
          </div>
          <button type="button" className="btn sm" style={{ marginTop: 'var(--s-3)' }}
            onClick={() => setLineItems(l => [...l, { description: '', amount: 0 }])}>
            ＋ Add line
          </button>
        </Card>

        <div className="totals-card">
          <div className="totals-row grand">
            <span className="lbl">Total</span>
            <span className="val">{money(total)}</span>
          </div>
        </div>

        {invoice ? (
          <Card>
            <SectionTitle>Payments</SectionTitle>
            <div className="row between" style={{ marginTop: 'var(--s-2)' }}>
              <span className="sb">Paid</span><span className="nm">{money(invoice.amountPaid || 0)}</span>
            </div>
            <div className="row between" style={{ marginTop: 'var(--s-1)' }}>
              <span className="sb">Balance</span>
              <span className="nm" style={{ color: invoice.balance === 0 ? 'var(--green-700)' : 'var(--red-600)' }}>
                {money(invoice.balance || 0)}
              </span>
            </div>

            <div className="col" style={{ gap: 'var(--s-1)', marginTop: 'var(--s-3)' }}>
              {(invoice.payments || []).length
                ? invoice.payments.map((p, i) => (
                    <div className="row between" key={i} style={{ fontSize: 'var(--fs-sm)' }}>
                      <span>{fmtDate(p.at)} · {p.method}{p.note ? ` · ${p.note}` : ''}</span>
                      <span className="nm">{money(p.amount)}</span>
                    </div>
                  ))
                : <Empty>No payments yet.</Empty>}
            </div>

            {invoice.balance > 0 ? (
              <div className="row-fields" style={{ '--cols': referenceLabel ? 4 : 3, marginTop: 'var(--s-3)', alignItems: 'end' }}>
                <Field label="Amount" id="payamt">
                  <input id="payamt" className="fi" type="number" min="0" step="0.01"
                    value={payAmount} onChange={e => setPayAmount(e.target.value)} />
                </Field>
                <Field label="Method" id="paymethod">
                  <SelectMenu
                    id="paymethod"
                    value={payMethod}
                    onChange={setMethod}
                    options={METHODS.map(method => ({ value: method, label: method.replace('_', ' ') }))}
                  />
                </Field>
                {referenceLabel ? (
                  <Field label={referenceLabel} id="payref">
                    <input id="payref" className="fi"
                      value={payReference} onChange={e => setPayReference(e.target.value)} />
                  </Field>
                ) : null}
                <button className="btn pri" onClick={recordPayment}>Record</button>
              </div>
            ) : (
              <div className="note ok" style={{ marginTop: 'var(--s-3)' }}>Invoice fully paid.</div>
            )}
          </Card>
        ) : null}

        <div className="btn-row">
          <button className="btn" onClick={onPreview} disabled={busy}>Save &amp; preview</button>
          <button className="btn pri" onClick={onSend} disabled={busy}>Send to customer</button>
        </div>
      </div>
    </>
  );
}
